import { useHingeHealthSecurityContext } from '@hinge-health/react-component-library';
import Papa from 'papaparse';
import { useEffect, useState } from 'react';
import { OktaRole } from '../../../utils/okta-roles';

export const BULK_ADJUSTMENTS_CATEGORY = 'bulkAdjustments';

export const ADJUSTMENT_UPLOAD_ROLES: OktaRole[] = [
  OktaRole.ADJUSTMENT_UPLOAD,
  OktaRole.ACCOUNTING_READ,
];

// The token library types roles as unknown, so narrow it here.
export const hasAdjustmentUploadAccess = (roles: unknown): boolean =>
  Array.isArray(roles) &&
  ADJUSTMENT_UPLOAD_ROLES.some(role => roles.includes(role));

export const useAdjustmentUploadAccess = (): boolean => {
  const { hingeHealthAuthState } = useHingeHealthSecurityContext();
  return hasAdjustmentUploadAccess(
    hingeHealthAuthState?.accessToken?.claims?.roles,
  );
};

// Refuse oversize files before the upload starts.
export const ADJUSTMENT_MAX_FILE_BYTES = 100 * 1024 * 1024;

// NetSuite reads the quarter and description from the file name. The
// description is limited to safe characters so the name cannot carry a path or
// a quote into the S3 key, the part names, or the database row.
export const ADJUSTMENT_FILE_NAME_PATTERN =
  /^Q[1-4]-\d{4}_[A-Za-z0-9_-]+\.csv$/;

export const ADJUSTMENT_FILE_NAME_TEMPLATE = 'Q<N>-<YYYY>_<description>.csv';

export const ADJUSTMENT_FILE_NAME_ERROR = `Name must be formatted like ${ADJUSTMENT_FILE_NAME_TEMPLATE}`;

export const getAdjustmentFileNameError = (
  fileCategory: string,
  file: File | null,
): string | undefined => {
  if (fileCategory !== BULK_ADJUSTMENTS_CATEGORY || !file) {
    return undefined;
  }

  return ADJUSTMENT_FILE_NAME_PATTERN.test(file.name)
    ? undefined
    : ADJUSTMENT_FILE_NAME_ERROR;
};

export const ADJUSTMENT_HEADER_READ_ERROR =
  'The file could not be read. Check that it is a CSV file.';

// Reads only the header line. The billing service checks the header again
// after the upload, so this only saves the uploader a failed upload.
export const readAdjustmentHeader = (file: File): Promise<string[]> =>
  new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      preview: 1,
      chunkSize: 64 * 1024,
      complete: results => {
        const [first = '', ...rest] = results.data[0] ?? [];
        // Excel "CSV UTF-8" exports start with a BOM.
        resolve([first.replace(/^\uFEFF/, ''), ...rest]);
      },
      error: reject,
    });
  });

// NetSuite maps columns by exact name, so a column cased differently is
// reported apart from a missing one.
export const getAdjustmentHeaderError = (
  header: string[],
  requiredColumns: string[],
): string | undefined => {
  const present = new Set(header);
  const byLowerCaseName = new Map(
    header.map(column => [column.toLowerCase(), column]),
  );
  const missing: string[] = [];
  const miscased: string[] = [];
  requiredColumns.forEach(column => {
    if (present.has(column)) {
      return;
    }
    const uploaded = byLowerCaseName.get(column.toLowerCase());
    if (uploaded === undefined) {
      missing.push(column);
    } else {
      miscased.push(`${uploaded} (expected ${column})`);
    }
  });

  const problems = [
    missing.length > 0 ? `Missing columns: ${missing.join(', ')}.` : '',
    miscased.length > 0
      ? `Column names must match case exactly: ${miscased.join(', ')}.`
      : '',
  ].filter(Boolean);
  return problems.length > 0 ? problems.join(' ') : undefined;
};

interface AdjustmentHeaderCheck {
  headerError?: string;
  isCheckingHeader: boolean;
}

export const useAdjustmentHeaderCheck = (
  fileCategory: string,
  file: File | null,
  requiredColumns: string[],
): AdjustmentHeaderCheck => {
  const [result, setResult] = useState<{ file: File; error?: string } | null>(
    null,
  );
  // Without the column list there is nothing to check against; the billing
  // service still checks the header.
  const isChecked =
    fileCategory === BULK_ADJUSTMENTS_CATEGORY &&
    file !== null &&
    requiredColumns.length > 0;

  useEffect(() => {
    if (!isChecked || !file) {
      return undefined;
    }
    let isCurrent = true;
    readAdjustmentHeader(file).then(
      header => {
        if (isCurrent) {
          setResult({
            file,
            error: getAdjustmentHeaderError(header, requiredColumns),
          });
        }
      },
      () => {
        if (isCurrent) {
          setResult({ file, error: ADJUSTMENT_HEADER_READ_ERROR });
        }
      },
    );
    return (): void => {
      isCurrent = false;
    };
  }, [isChecked, file, requiredColumns]);

  if (!isChecked) {
    return { isCheckingHeader: false };
  }
  if (result?.file !== file) {
    return { isCheckingHeader: true };
  }
  return { headerError: result.error, isCheckingHeader: false };
};
